import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MediaGrid from '@/components/feature/MediaGrid';
import SectionTitle from '@/components/feature/SectionTitle';
import PlayModal from './components/PlayModal';
import { getMediaById, getRelated } from '@/lib/media';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const [playOpen, setPlayOpen] = useState(false);

  if (!item) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <i className="ri-error-warning-line text-5xl text-foreground-300" />
        <h1 className="mt-4 text-xl font-bold text-foreground-950 font-heading">影视信息暂不可用</h1>
        <p className="mt-2 text-sm text-foreground-500">该作品可能已下架或链接有误，请返回首页浏览其他内容</p>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(item, 6);
  const isTv = item.type === 'tv';
  const categoryLabel = isTv ? '电视剧' : '电影';
  const categoryTo = `/category/${item.type}`;

  const infoRows = [
    { label: '导演', value: item.director },
    { label: '主演', value: item.actors.join(' / ') },
    { label: '类型', value: item.genres.join(' · ') },
    { label: '地区', value: item.region },
    { label: '年份', value: String(item.year) },
    { label: isTv ? '集数' : '时长', value: isTv ? `${item.episodes} 集` : item.duration },
  ];

  return (
    <div className="w-full">
      <div className="relative w-full bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img src={item.cover} alt="" className="h-full w-full object-top opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60" />
        </div>

        <div className="relative mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: categoryLabel, to: categoryTo }, { label: item.title }]} />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="mx-auto h-72 w-48 shrink-0 overflow-hidden rounded-lg bg-primary-900 md:mx-0 md:h-80 md:w-56">
              <img
                src={item.cover}
                alt={`${item.title}海报`}
                title={`${item.title} - 八戒影院影视详情`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-background-50 font-heading md:text-4xl">{item.title}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-2.5 py-1 text-sm font-bold text-primary-950">
                  <i className="ri-star-fill" />
                  {item.rating.toFixed(1)}
                </span>
                {item.status && (
                  <span className="rounded-md bg-accent-500 px-2.5 py-1 text-sm font-medium text-background-50">
                    {item.status}
                  </span>
                )}
                <span className="flex items-center gap-1 rounded-md bg-primary-900 px-2.5 py-1 text-sm text-background-200">
                  <i className="ri-play-circle-line" />
                  {item.views}次播放
                </span>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-2 text-sm">
                    <span className="shrink-0 text-background-400">{row.label}</span>
                    <span className="text-background-100">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPlayOpen(true)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-xl" />
                  {isTv ? '开始追剧' : '立即观看'}
                </button>
                <Link
                  to={categoryTo}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50/10 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/20"
                >
                  <i className={isTv ? 'ri-tv-2-line text-lg' : 'ri-film-line text-lg'} />
                  更多{categoryLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <section>
          <SectionTitle title="剧情简介" />
          <div className="rounded-lg bg-background-100 p-5 md:p-6">
            <p className="text-sm leading-loose text-foreground-700 md:text-base">{item.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-10">
          <SectionTitle
            title="相关推荐"
            subtitle="根据类型与地区为你推荐"
            moreTo={categoryTo}
          />
          <MediaGrid items={related} columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" />
        </section>
      </div>

      <PlayModal item={item} open={playOpen} onClose={() => setPlayOpen(false)} />
    </div>
  );
}